import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation, Navigate } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import './App.css';

// Authentication Component for all user types
const Auth = ({ onLogin }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [userType, setUserType] = useState('student');
  const [formData, setFormData] = useState({
    id: '',
    email: '',
    password: '',
    confirmPassword: '',
    fullName: '',
    faculty: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleUserTypeChange = (type) => {
    setUserType(type);
    setFormData({
      id: '',
      email: '',
      password: '',
      confirmPassword: '',
      fullName: '',
      faculty: ''
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (isLogin) {
      // Login logic
      if (formData.id && formData.password) {
        onLogin({
          id: formData.id,
          name: formData.fullName || `User ${formData.id}`,
          role: userType,
          faculty: formData.faculty || 'ICT'
        });
      } else {
        alert('Please enter ID and Password');
      }
    } else {
      // Register logic
      if (formData.password !== formData.confirmPassword) {
        alert('Passwords do not match!');
        return;
      }
      if (formData.id && formData.email && formData.password && formData.fullName) {
        alert('Registration successful! Please login.');
        setIsLogin(true);
      } else {
        alert('Please fill all required fields');
      }
    }
  };

  const getUserIdLabel = () => {
    switch (userType) {
      case 'student': return 'Student ID';
      case 'lecturer': return 'Lecturer ID';
      case 'principal_lecturer': return 'PRL ID';
      case 'program_leader': return 'PL ID';
      default: return 'ID';
    }
  };

  return (
    <div className="container-fluid d-flex justify-content-center align-items-center min-vh-100 auth-bg">
      <div className="card shadow-lg" style={{ width: '450px' }}>
        <div className="card-header bg-primary text-white text-center py-3">
          <h4 className="mb-0">
            <i className="fas fa-user me-2"></i>
            LUCT Portal - {isLogin ? 'Login' : 'Register'}
          </h4>
        </div>
        <div className="card-body p-4">
          <div className="mb-3">
            <label className="form-label">Login As:</label>
            <div className="btn-group w-100" role="group">
              {['student', 'lecturer', 'principal_lecturer', 'program_leader'].map((type) => (
                <button
                  key={type}
                  type="button"
                  className={`btn ${userType === type ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => handleUserTypeChange(type)}
                >
                  {type.replace('_', ' ').toUpperCase()}
                </button>
              ))}
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            {!isLogin && (
              <div className="mb-3">
                <label className="form-label">Full Name</label>
                <input 
                  type="text" 
                  className="form-control" 
                  name="fullName" 
                  value={formData.fullName} 
                  onChange={handleInputChange}
                  required 
                />
              </div>
            )}
            
            <div className="mb-3">
              <label className="form-label">{getUserIdLabel()}</label>
              <input 
                type="text" 
                className="form-control" 
                name="id" 
                value={formData.id} 
                onChange={handleInputChange}
                required 
              />
            </div>
            
            {!isLogin && (
              <>
                <div className="mb-3">
                  <label className="form-label">Email</label>
                  <input 
                    type="email" 
                    className="form-control" 
                    name="email" 
                    value={formData.email} 
                    onChange={handleInputChange}
                    required 
                  />
                </div>
                
                <div className="mb-3">
                  <label className="form-label">Faculty</label>
                  <select className="form-control" name="faculty" value={formData.faculty} onChange={handleInputChange} required>
                    <option value="">Select Faculty</option>
                    <option value="ICT">Faculty of ICT</option>
                    <option value="Business">Faculty of Business</option>
                    <option value="Design">Faculty of Design</option>
                  </select>
                </div>
              </>
            )}
            
            <div className="mb-3">
              <label className="form-label">Password</label>
              <input 
                type="password" 
                className="form-control" 
                name="password" 
                value={formData.password} 
                onChange={handleInputChange}
                required 
              />
            </div>
            
            {!isLogin && (
              <div className="mb-3">
                <label className="form-label">Confirm Password</label>
                <input 
                  type="password" 
                  className="form-control" 
                  name="confirmPassword" 
                  value={formData.confirmPassword} 
                  onChange={handleInputChange}
                  required 
                />
              </div>
            )}
            
            <div className="d-grid mb-3">
              <button type="submit" className="btn btn-primary">
                {isLogin ? 'Login' : 'Register'}
              </button>
            </div>
            
            <div className="text-center">
              <button 
                type="button" 
                className="btn btn-link" 
                onClick={() => setIsLogin(!isLogin)}
              >
                {isLogin ? "Don't have an account? Register" : "Already have an account? Login"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Student Dashboard
const StudentDashboard = ({ user, courses, attendance }) => {
  return (
    <div className="row">
      <div className="col-12">
        <div className="card">
          <div className="card-header bg-primary text-white">
            <h5 className="mb-0">
              <i className="fas fa-tachometer-alt me-2"></i>
              Student Dashboard - Welcome, {user.name}
            </h5>
          </div>
          <div className="card-body">
            <div className="row mb-4">
              <div className="col-md-3">
                <div className="card text-center bg-light">
                  <div className="card-body">
                    <h3>{courses.length}</h3>
                    <p className="mb-0">Enrolled Courses</p>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card text-center bg-light">
                  <div className="card-body">
                    <h3>85%</h3>
                    <p className="mb-0">Overall Attendance</p>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card text-center bg-light">
                  <div className="card-body">
                    <h3>4.2/5</h3>
                    <p className="mb-0">Average Rating</p>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card text-center bg-light">
                  <div className="card-body">
                    <h3>12</h3>
                    <p className="mb-0">Pending Assignments</p>
                  </div>
                </div>
              </div>
            </div>

            <h6>My Courses</h6>
            <div className="row">
              {courses.map((course, index) => (
                <div key={index} className="col-md-4 mb-3">
                  <div className="card h-100">
                    <div className="card-body">
                      <h6 className="card-title">{course.code} - {course.name}</h6>
                      <p className="card-text">
                        <small className="text-muted">
                          <i className="fas fa-user-tie me-1"></i> {course.lecturer}<br/>
                          <i className="fas fa-calendar me-1"></i> {course.schedule}<br/>
                          <i className="fas fa-map-marker-alt me-1"></i> {course.venue}
                        </small>
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const StudentMonitoring = ({ user, attendance }) => {
  const studentAttendance = attendance.filter(a => a.studentId === user.id);

  return (
    <div className="row">
      <div className="col-12">
        <div className="card">
          <div className="card-header">
            <h5 className="mb-0">
              <i className="fas fa-chart-line me-2"></i>
              Attendance Monitoring
            </h5>
          </div>
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-striped">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Course</th>
                    <th>Status</th>
                    <th>Time</th>
                    <th>Remarks</th>
                  </tr>
                </thead>
                <tbody>
                  {studentAttendance.map((record, index) => (
                    <tr key={index}>
                      <td>{record.date}</td>
                      <td>{record.course}</td>
                      <td>
                        <span className={`badge bg-${record.status === 'Present' ? 'success' : 'danger'}`}>
                          {record.status}
                        </span>
                      </td>
                      <td>{record.time}</td>
                      <td>{record.remarks}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="row mt-4">
              <div className="col-md-6">
                <h6>Attendance Summary</h6>
                <div className="card">
                  <div className="card-body">
                    <div className="mb-3">
                      <strong>Total Classes: </strong> {studentAttendance.length}
                    </div>
                    <div className="mb-3">
                      <strong>Present: </strong> 
                      {studentAttendance.filter(a => a.status === 'Present').length}
                    </div>
                    <div className="mb-3">
                      <strong>Absent: </strong> 
                      {studentAttendance.filter(a => a.status === 'Absent').length}
                    </div>
                    <div className="mb-3">
                      <strong>Attendance Rate: </strong> 
                      {studentAttendance.length > 0 
                        ? Math.round((studentAttendance.filter(a => a.status === 'Present').length / studentAttendance.length) * 100) 
                        : 0}%
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <h6>Recent Notifications</h6>
                <div className="list-group">
                  <div className="list-group-item">
                    <small className="text-muted">Today</small>
                    <p className="mb-1">Web Applications class at 10:30 in MultiMedia 5</p>
                  </div>
                  <div className="list-group-item">
                    <small className="text-muted">Yesterday</small>
                    <p className="mb-1">Assignment 2 submission due this Friday</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const StudentRating = ({ user, courses, onRateCourse }) => {
  const [ratings, setRatings] = useState({});
  const [feedback, setFeedback] = useState({});

  const handleRatingChange = (courseCode, rating) => {
    setRatings({ ...ratings, [courseCode]: rating });
  };

  const handleFeedbackChange = (courseCode, text) => {
    setFeedback({ ...feedback, [courseCode]: text });
  };

  const handleSubmitRating = (courseCode) => {
    if (ratings[courseCode]) {
      onRateCourse(courseCode, ratings[courseCode], feedback[courseCode] || '');
      alert('Rating submitted successfully!');
      setRatings({ ...ratings, [courseCode]: 0 });
      setFeedback({ ...feedback, [courseCode]: '' });
    } else {
      alert('Please select a rating');
    }
  };

  return (
    <div className="row">
      <div className="col-12">
        <div className="card">
          <div className="card-header">
            <h5 className="mb-0">
              <i className="fas fa-star me-2"></i>
              Course Ratings & Feedback
            </h5>
          </div>
          <div className="card-body">
            {courses.map((course, index) => (
              <div key={index} className="card mb-3">
                <div className="card-body">
                  <h6>{course.code} - {course.name}</h6>
                  <p className="text-muted">Lecturer: {course.lecturer}</p>
                  
                  <div className="mb-3">
                    <label className="form-label">Rate this course (1-5 stars):</label>
                    <div>
                      {[1, 2, 3, 4, 5].map(star => (
                        <button
                          key={star}
                          type="button"
                          className={`btn btn-sm ${ratings[course.code] >= star ? 'btn-warning' : 'btn-outline-warning'} me-1`}
                          onClick={() => handleRatingChange(course.code, star)}
                        >
                          <i className="fas fa-star"></i>
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  <div className="mb-3">
                    <label className="form-label">Your Feedback:</label>
                    <textarea
                      className="form-control"
                      rows="3"
                      value={feedback[course.code] || ''}
                      onChange={(e) => handleFeedbackChange(course.code, e.target.value)}
                      placeholder="Share your experience with this course..."
                    ></textarea>
                  </div>
                  
                  <button
                    className="btn btn-primary"
                    onClick={() => handleSubmitRating(course.code)}
                  >
                    Submit Rating
                  </button>
                </div>
              </div>
            ))}

            <div className="card">
              <div className="card-header">
                <h6>Course Ratings Overview</h6>
              </div>
              <div className="card-body">
                {courses.map((course, index) => (
                  <div key={index} className="mb-2">
                    <strong>{course.code}:</strong>
                    <div className="d-inline-block ms-2">
                      {[...Array(5)].map((_, i) => (
                        <i
                          key={i}
                          className={`fas fa-star ${i < Math.floor(course.avgRating || 0) ? 'text-warning' : 'text-muted'}`}
                        ></i>
                      ))}
                      <span className="ms-2">{course.avgRating || 'No ratings yet'}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Lecturer Components
const LecturerDashboard = ({ user }) => {
   const handleExport = () => {
  const lecturerName = user.name; // assuming you already have user.name from login
  console.log(`📂 Export button clicked by ${lecturerName}`);

  window.open(`http://localhost:5000/api/export/reports?lecturer=${encodeURIComponent(lecturerName)}`);
};
  return (
    <div className="row">
      <div className="col-12">
        <div className="card">
          <div className="card-header bg-primary text-white">
            <h5 className="mb-0">
              <i className="fas fa-tachometer-alt me-2"></i>
              Lecturer Dashboard - Welcome, {user.name}
            </h5>
          </div>
          <div className="card-body">
            <div className="row mb-4">
              <div className="col-md-3">
                <div className="card text-center bg-light">
                  <div className="card-body">
                    <h3>5</h3>
                    <p className="mb-0">Active Classes</p>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card text-center bg-light">
                  <div className="card-body">
                    <h3>24</h3>
                    <p className="mb-0">Reports Submitted</p>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card text-center bg-light">
                  <div className="card-body">
                    <h3>4.5/5</h3>
                    <p className="mb-0">Average Rating</p>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card text-center bg-light">
                  <div className="card-body">
                    <h3>85%</h3>
                    <p className="mb-0">Attendance Rate</p>
                  </div>
                </div>
              </div>
            </div>
            
               {/* Export Excel Button */}
            <button
              className="btn btn-success mb-3"
              onClick={() => window.open("http://localhost:5000/api/export/reports")}
            >
              Export Reports to Excel
            </button>

            {/* Recent Activities */}
            <h6>Recent Activities</h6>
            <div className="list-group">
              <div className="list-group-item">
                <small className="text-muted">Today, 10:30 AM</small>
                <p className="mb-1">Submitted report for DIWA2110 - Web Applications</p>
              </div>
              <div className="list-group-item">
                <small className="text-muted">Yesterday, 2:00 PM</small>
                <p className="mb-1">Received feedback from Principal Lecturer</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const LecturerClasses = ({ user }) => {
  const classes = [
    { code: 'DIWA2110', name: 'Web Applications', schedule: 'Mon 10:30-12:30', venue: 'MultiMedia 5', students: 45 },
    { code: 'DIPR2110', name: 'OOP Programming', schedule: 'Wed 14:00-16:00', venue: 'MultiMedia 4', students: 38 },
    { code: 'DIDB2110', name: 'Database Systems', schedule: 'Fri 09:00-11:00', venue: 'MultiMedia 1', students: 42 }
  ];

  return (
    <div className="row">
      <div className="col-12">
        <div className="card">
          <div className="card-header">
            <h5 className="mb-0">
              <i className="fas fa-chalkboard me-2"></i>
              My Classes
            </h5>
          </div>
          <div className="card-body">
            <div className="row">
              {classes.map((cls, index) => (
                <div key={index} className="col-md-4 mb-3">
                  <div className="card h-100">
                    <div className="card-body">
                      <h6 className="card-title">{cls.code} - {cls.name}</h6>
                      <p className="card-text">
                        <small className="text-muted">
                          <i className="fas fa-calendar me-1"></i> {cls.schedule}<br/>
                          <i className="fas fa-map-marker-alt me-1"></i> {cls.venue}<br/>
                          <i className="fas fa-users me-1"></i> {cls.students} students
                        </small>
                      </p>
                      <button className="btn btn-primary btn-sm">View Details</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const LecturerReports = ({ user, onAddReport }) => {
  const [showForm, setShowForm] = useState(false);
  const [reportData, setReportData] = useState({
    facultyName: 'ICT',
    className: '',
    weekOfReporting: '',
    dateOfLecture: '',
    courseName: '',
    courseCode: '',
    lecturerName: user.name,
    actualStudentsPresent: '',
    totalRegisteredStudents: '',
    venue: '',
    scheduledTime: '',
    topicTaught: '',
    learningOutcomes: '',
    recommendations: ''
  });

  const reports = [
    { id: 1, courseCode: 'DIWA2110', date: '2025-09-15', studentsPresent: 42, totalStudents: 45, status: 'Submitted' },
    { id: 2, courseCode: 'DIPR2110', date: '2025-09-10', studentsPresent: 35, totalStudents: 38, status: 'Reviewed' }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setReportData({ ...reportData, [name]: value });
  };

  const handleSubmitReport = (e) => {
    e.preventDefault();
    onAddReport(reportData);
    setShowForm(false);
    setReportData({
      facultyName: 'ICT',
      className: '',
      weekOfReporting: '',
      dateOfLecture: '',
      courseName: '',
      courseCode: '',
      lecturerName: user.name,
      actualStudentsPresent: '',
      totalRegisteredStudents: '',
      venue: '',
      scheduledTime: '',
      topicTaught: '',
      learningOutcomes: '',
      recommendations: ''
    });
    alert('Report submitted successfully!');
  };

  return (
    <div className="row">
      <div className="col-12">
        <div className="card">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h5 className="mb-0">
              <i className="fas fa-file-alt me-2"></i>
              Lecture Reports
            </h5>
            <button className="btn btn-primary" onClick={() => setShowForm(!showForm)}>
              <i className="fas fa-plus me-1"></i> New Report
            </button>
          </div>
          <div className="card-body">
            {showForm && (
              <div className="card mb-4">
                <div className="card-header">
                  <h6>New Lecture Report</h6>
                </div>
                <div className="card-body">
                  <form onSubmit={handleSubmitReport}>
                    <div className="row">
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">Faculty Name</label>
                          <input type="text" className="form-control" name="facultyName" value={reportData.facultyName} onChange={handleInputChange} required />
                        </div>
                        <div className="mb-3">
                          <label className="form-label">Class Name</label>
                          <input type="text" className="form-control" name="className" value={reportData.className} onChange={handleInputChange} required />
                        </div>
                        <div className="mb-3">
                          <label className="form-label">Week of Reporting</label>
                          <input type="text" className="form-control" name="weekOfReporting" value={reportData.weekOfReporting} onChange={handleInputChange} required />
                        </div>
                        <div className="mb-3">
                          <label className="form-label">Date of Lecture</label>
                          <input type="date" className="form-control" name="dateOfLecture" value={reportData.dateOfLecture} onChange={handleInputChange} required />
                        </div>
                        <div className="mb-3">
                          <label className="form-label">Course Name</label>
                          <input type="text" className="form-control" name="courseName" value={reportData.courseName} onChange={handleInputChange} required />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">Course Code</label>
                          <input type="text" className="form-control" name="courseCode" value={reportData.courseCode} onChange={handleInputChange} required />
                        </div>
                        <div className="mb-3">
                          <label className="form-label">Actual Students Present</label>
                          <input type="number" className="form-control" name="actualStudentsPresent" value={reportData.actualStudentsPresent} onChange={handleInputChange} required />
                        </div>
                        <div className="mb-3">
                          <label className="form-label">Total Registered Students</label>
                          <input type="number" className="form-control" name="totalRegisteredStudents" value={reportData.totalRegisteredStudents} onChange={handleInputChange} required />
                        </div>
                        <div className="mb-3">
                          <label className="form-label">Venue</label>
                          <input type="text" className="form-control" name="venue" value={reportData.venue} onChange={handleInputChange} required />
                        </div>
                        <div className="mb-3">
                          <label className="form-label">Scheduled Time</label>
                          <input type="text" className="form-control" name="scheduledTime" value={reportData.scheduledTime} onChange={handleInputChange} required />
                        </div>
                      </div>
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Topics Taught</label>
                      <textarea className="form-control" name="topicTaught" value={reportData.topicTaught} onChange={handleInputChange} required />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Learning Outcomes</label>
                      <textarea className="form-control" name="learningOutcomes" value={reportData.learningOutcomes} onChange={handleInputChange} required />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Recommendations</label>
                      <textarea className="form-control" name="recommendations" value={reportData.recommendations} onChange={handleInputChange} required />
                    </div>
                    <div className="d-grid">
                      <button type="submit" className="btn btn-success">Submit Report</button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            <div className="table-responsive">
              <table className="table table-striped">
                <thead>
                  <tr>
                    <th>Report ID</th>
                    <th>Course Code</th>
                    <th>Date</th>
                    <th>Students Present</th>
                    <th>Total Students</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {reports.map((report) => (
                    <tr key={report.id}>
                      <td>#{report.id}</td>
                      <td>{report.courseCode}</td>
                      <td>{report.date}</td>
                      <td>{report.studentsPresent}</td>
                      <td>{report.totalStudents}</td>
                      <td>
                        <span className={`badge bg-${report.status === 'Submitted' ? 'warning' : 'success'}`}>
                          {report.status}
                        </span>
                      </td>
                      <td>
                        <button className="btn btn-sm btn-outline-primary">View</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const LecturerMonitoring = ({ user }) => {
  const attendanceData = [
    { course: 'DIWA2110', date: '2025-09-15', present: 42, absent: 3, rate: '93%' },
    { course: 'DIPR2110', date: '2025-09-10', present: 35, absent: 3, rate: '92%' },
    { course: 'DIDB2110', date: '2025-09-08', present: 40, absent: 2, rate: '95%' }
  ];

  return (
    <div className="row">
      <div className="col-12">
        <div className="card">
          <div className="card-header">
            <h5 className="mb-0">
              <i className="fas fa-chart-bar me-2"></i>
              Class Monitoring
            </h5>
          </div>
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-striped">
                <thead>
                  <tr>
                    <th>Course</th>
                    <th>Date</th>
                    <th>Present</th>
                    <th>Absent</th>
                    <th>Attendance Rate</th>
                    <th>Trend</th>
                  </tr>
                </thead>
                <tbody>
                  {attendanceData.map((data, index) => (
                    <tr key={index}>
                      <td>{data.course}</td>
                      <td>{data.date}</td>
                      <td>{data.present}</td>
                      <td>{data.absent}</td>
                      <td>{data.rate}</td>
                      <td>
                        <span className="text-success">
                          <i className="fas fa-arrow-up"></i> Improving
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const LecturerRating = ({ user }) => {
  const ratings = [
    { course: 'DIWA2110', rating: 4.5, feedback: 'Excellent teaching methodology', date: '2025-09-10' },
    { course: 'DIPR2110', rating: 4.2, feedback: 'Clear explanations', date: '2025-09-05' }
  ];

  return (
    <div className="row">
      <div className="col-12">
        <div className="card">
          <div className="card-header">
            <h5 className="mb-0">
              <i className="fas fa-star me-2"></i>
              Student Ratings & Feedback
            </h5>
          </div>
          <div className="card-body">
            <div className="row mb-4">
              <div className="col-md-3 text-center">
                <div className="card bg-light">
                  <div className="card-body">
                    <h2>4.5</h2>
                    <p>Average Rating</p>
                  </div>
                </div>
              </div>
              <div className="col-md-3 text-center">
                <div className="card bg-light">
                  <div className="card-body">
                    <h2>42</h2>
                    <p>Total Ratings</p>
                  </div>
                </div>
              </div>
            </div>

            <h6>Recent Feedback</h6>
            {ratings.map((rating, index) => (
              <div key={index} className="card mb-3">
                <div className="card-body">
                  <div className="d-flex justify-content-between">
                    <h6>{rating.course}</h6>
                    <span className="text-warning">
                      {[...Array(5)].map((_, i) => (
                        <i key={i} className={`fas fa-star ${i < Math.floor(rating.rating) ? 'text-warning' : 'text-muted'}`}></i>
                      ))}
                      <span className="ms-2">{rating.rating}</span>
                    </span>
                  </div>
                  <p className="mb-1">{rating.feedback}</p>
                  <small className="text-muted">{rating.date}</small>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// Principal Lecturer Components
const PrincipalLecturerDashboard = ({ user }) => {
  return (
    <div className="row">
      <div className="col-12">
        <div className="card">
          <div className="card-header bg-primary text-white">
            <h5 className="mb-0">
              <i className="fas fa-tachometer-alt me-2"></i>
              Principal Lecturer Dashboard - Welcome, {user.name}
            </h5>
          </div>
          <div className="card-body">
            <div className="row mb-4">
              <div className="col-md-3">
                <div className="card text-center bg-light">
                  <div className="card-body">
                    <h3>8</h3>
                    <p className="mb-0">Lecturers</p>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card text-center bg-light">
                  <div className="card-body">
                    <h3>15</h3>
                    <p className="mb-0">Courses</p>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card text-center bg-light">
                  <div className="card-body">
                    <h3>42</h3>
                    <p className="mb-0">Reports to Review</p>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card text-center bg-light">
                  <div className="card-body">
                    <h3>92%</h3>
                    <p className="mb-0">Attendance Rate</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const PRLCourses = ({ user }) => {
  const courses = [
    { code: 'DIWA2110', name: 'Web Applications', lecturer: 'Mr. Thokoana', students: 45, status: 'Active' },
    { code: 'DIPR2110', name: 'OOP Programming', lecturer: 'Mr. Takalimane', students: 38, status: 'Active' },
    { code: 'DIDB2110', name: 'Database Systems', lecturer: 'Ms. Sekopo', students: 42, status: 'Active' }
  ];

  return (
    <div className="row">
      <div className="col-12">
        <div className="card">
          <div className="card-header">
            <h5 className="mb-0">
              <i className="fas fa-book me-2"></i>
              Courses Under My Stream
            </h5>
          </div>
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-striped">
                <thead>
                  <tr>
                    <th>Course Code</th>
                    <th>Course Name</th>
                    <th>Lecturer</th>
                    <th>Students</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {courses.map((course, index) => (
                    <tr key={index}>
                      <td>{course.code}</td>
                      <td>{course.name}</td>
                      <td>{course.lecturer}</td>
                      <td>{course.students}</td>
                      <td>
                        <span className="badge bg-success">{course.status}</span>
                      </td>
                      <td>
                        <button className="btn btn-sm btn-outline-primary me-1">View</button>
                        <button className="btn btn-sm btn-outline-info">Reports</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const PRLReports = ({ user }) => {
  const reports = [
    { id: 1, lecturer: 'Mr. Thokoana', course: 'DIWA2110', date: '2025-09-15', status: 'Pending Review' },
    { id: 2, lecturer: 'Mr. Takalimane', course: 'DIPR2110', date: '2025-09-10', status: 'Reviewed' }
  ];

  const [feedback, setFeedback] = useState('');

  const handleAddFeedback = (reportId) => {
    if (feedback.trim()) {
      alert(`Feedback added to report #${reportId}`);
      setFeedback('');
    } else {
      alert('Please enter feedback');
    }
  };

  return (
    <div className="row">
      <div className="col-12">
        <div className="card">
          <div className="card-header">
            <h5 className="mb-0">
              <i className="fas fa-file-alt me-2"></i>
              Lecture Reports Review
            </h5>
          </div>
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-striped">
                <thead>
                  <tr>
                    <th>Report ID</th>
                    <th>Lecturer</th>
                    <th>Course</th>
                    <th>Date</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {reports.map((report) => (
                    <tr key={report.id}>
                      <td>#{report.id}</td>
                      <td>{report.lecturer}</td>
                      <td>{report.course}</td>
                      <td>{report.date}</td>
                      <td>
                        <span className={`badge bg-${report.status === 'Pending Review' ? 'warning' : 'success'}`}>
                          {report.status}
                        </span>
                      </td>
                      <td>
                        <button className="btn btn-sm btn-outline-primary me-1">View</button>
                        <button className="btn btn-sm btn-outline-info" data-bs-toggle="modal" data-bs-target="#feedbackModal">Add Feedback</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Feedback Modal */}
            <div className="modal fade" id="feedbackModal" tabIndex="-1">
              <div className="modal-dialog">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">Add Feedback</h5>
                    <button type="button" className="btn-close" data-bs-dismiss="modal"></button>
                  </div>
                  <div className="modal-body">
                    <div className="mb-3">
                      <label className="form-label">Your Feedback</label>
                      <textarea 
                        className="form-control" 
                        rows="4" 
                        value={feedback}
                        onChange={(e) => setFeedback(e.target.value)}
                        placeholder="Enter your feedback for this report..."
                      ></textarea>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">Close</button>
                    <button type="button" className="btn btn-primary" onClick={() => handleAddFeedback(1)}>Submit Feedback</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Program Leader Components
const ProgramLeaderDashboard = ({ user }) => {
  return (
    <div className="row">
      <div className="col-12">
        <div className="card">
          <div className="card-header bg-primary text-white">
            <h5 className="mb-0">
              <i className="fas fa-tachometer-alt me-2"></i>
              Program Leader Dashboard - Welcome, {user.name}
            </h5>
          </div>
          <div className="card-body">
            <div className="row mb-4">
              <div className="col-md-3">
                <div className="card text-center bg-light">
                  <div className="card-body">
                    <h3>12</h3>
                    <p className="mb-0">Programs</p>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card text-center bg-light">
                  <div className="card-body">
                    <h3>25</h3>
                    <p className="mb-0">Lecturers</p>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card text-center bg-light">
                  <div className="card-body">
                    <h3>48</h3>
                    <p className="mb-0">Courses</p>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card text-center bg-light">
                  <div className="card-body">
                    <h3>15</h3>
                    <p className="mb-0">PRL Reports</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const PLCourses = ({ user }) => {
  const [showForm, setShowForm] = useState(false);
  const [courseData, setCourseData] = useState({
    code: '',
    name: '',
    lecturer: '',
    credits: '',
    semester: ''
  });

  const courses = [
    { code: 'DIWA2110', name: 'Web Applications', lecturer: 'Mr. Thokoana', credits: 3, semester: '1' },
    { code: 'DIPR2110', name: 'OOP Programming', lecturer: 'Mr. Takalimane', credits: 4, semester: '1' }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCourseData({ ...courseData, [name]: value });
  };

  const handleAddCourse = (e) => {
    e.preventDefault();
    alert(`Course ${courseData.code} added successfully!`);
    setShowForm(false);
    setCourseData({ code: '', name: '', lecturer: '', credits: '', semester: '' });
  };

  return (
    <div className="row">
      <div className="col-12">
        <div className="card">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h5 className="mb-0">
              <i className="fas fa-book me-2"></i>
              Course Management
            </h5>
            <button className="btn btn-primary" onClick={() => setShowForm(!showForm)}>
              <i className="fas fa-plus me-1"></i> Add Course
            </button>
          </div>
          <div className="card-body">
            {showForm && (
              <div className="card mb-4">
                <div className="card-header">
                  <h6>Add New Course</h6>
                </div>
                <div className="card-body">
                  <form onSubmit={handleAddCourse}>
                    <div className="row">
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">Course Code</label>
                          <input type="text" className="form-control" name="code" value={courseData.code} onChange={handleInputChange} required />
                        </div>
                        <div className="mb-3">
                          <label className="form-label">Course Name</label>
                          <input type="text" className="form-control" name="name" value={courseData.name} onChange={handleInputChange} required />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">Assigned Lecturer</label>
                          <select className="form-control" name="lecturer" value={courseData.lecturer} onChange={handleInputChange} required>
                            <option value="">Select Lecturer</option>
                            <option value="Mr. Thokoana">Mr. Thokoana</option>
                            <option value="Mr. Takalimane">Mr. Takalimane</option>
                            <option value="Ms. Sekopo">Ms. Sekopo</option>
                          </select>
                        </div>
                        <div className="mb-3">
                          <label className="form-label">Credits</label>
                          <input type="number" className="form-control" name="credits" value={courseData.credits} onChange={handleInputChange} required />
                        </div>
                      </div>
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Semester</label>
                      <select className="form-control" name="semester" value={courseData.semester} onChange={handleInputChange} required>
                        <option value="">Select Semester</option>
                        <option value="1">Semester 1</option>
                        <option value="2">Semester 2</option>
                      </select>
                    </div>
                    <div className="d-grid">
                      <button type="submit" className="btn btn-success">Add Course</button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            <div className="table-responsive">
              <table className="table table-striped">
                <thead>
                  <tr>
                    <th>Course Code</th>
                    <th>Course Name</th>
                    <th>Lecturer</th>
                    <th>Credits</th>
                    <th>Semester</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {courses.map((course, index) => (
                    <tr key={index}>
                      <td>{course.code}</td>
                      <td>{course.name}</td>
                      <td>{course.lecturer}</td>
                      <td>{course.credits}</td>
                      <td>Semester {course.semester}</td>
                      <td>
                        <button className="btn btn-sm btn-outline-primary me-1">Edit</button>
                        <button className="btn btn-sm btn-outline-danger">Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const PLReports = ({ user }) => {
  const reports = [
    { id: 1, prl: 'Dr. Molapo', program: 'BIT', date: '2025-09-15', status: 'Reviewed' },
    { id: 2, prl: 'Dr. Khotle', program: 'BSc IT', date: '2025-09-12', status: 'Pending' }
  ];

  return (
    <div className="row">
      <div className="col-12">
        <div className="card">
          <div className="card-header">
            <h5 className="mb-0">
              <i className="fas fa-file-alt me-2"></i>
              PRL Reports
            </h5>
          </div>
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-striped">
                <thead>
                  <tr>
                    <th>Report ID</th>
                    <th>PRL</th>
                    <th>Program</th>
                    <th>Date</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {reports.map((report) => (
                    <tr key={report.id}>
                      <td>#{report.id}</td>
                      <td>{report.prl}</td>
                      <td>{report.program}</td>
                      <td>{report.date}</td>
                      <td>
                        <span className={`badge bg-${report.status === 'Pending' ? 'warning' : 'success'}`}>
                          {report.status}
                        </span>
                      </td>
                      <td>
                        <button className="btn btn-sm btn-outline-primary">View</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Sidebar Components for different roles
const StudentSidebar = ({ user, onLogout }) => {
  const location = useLocation();
  
  return (
    <div className="col-md-2 sidebar d-none d-md-block bg-dark text-white">
      <div className="text-center mb-3 pt-3">
        <img 
          src=" https://tse3.mm.bing.net/th/id/OIP.FZjOcNetgkfRJhQkSR0acQHaD4?cb=12&rs=1&pid=ImgDetMain&o=7&rm=3" 
          alt="Limkokwing University" 
          className="sidebar-logo mb-2"
          style={{ maxWidth: '120px', height: 'auto' }}
        />
      </div>
      
      <div className="text-center mb-4">
        <div className="profile-placeholder bg-secondary rounded-circle mx-auto mb-2 d-flex align-items-center justify-content-center text-white">
          <i className="fas fa-user-graduate fa-2x"></i>
        </div>
        <h6>{user.name}</h6>
        <small>Student</small>
      </div>

      <nav className="nav flex-column">
        <Link to="/student/dashboard" className={`nav-link text-white ${location.pathname === '/student/dashboard' ? 'active bg-primary' : ''}`}>
          <i className="fas fa-tachometer-alt me-2"></i> Dashboard
        </Link>
        <Link to="/student/monitoring" className={`nav-link text-white ${location.pathname === '/student/monitoring' ? 'active bg-primary' : ''}`}>
          <i className="fas fa-chart-line me-2"></i> Monitoring
        </Link>
        <Link to="/student/rating" className={`nav-link text-white ${location.pathname === '/student/rating' ? 'active bg-primary' : ''}`}>
          <i className="fas fa-star me-2"></i> Rating
        </Link>
        <button className="nav-link text-white text-start border-0 bg-transparent" onClick={onLogout}>
          <i className="fas fa-sign-out-alt me-2"></i> Logout
        </button>
      </nav>
    </div>
  );
};

const LecturerSidebar = ({ user, onLogout }) => {
  const location = useLocation();
  
  return (
    <div className="col-md-2 sidebar d-none d-md-block bg-dark text-white">
      <div className="text-center mb-3 pt-3">
        <img 
          src="data:image/webp;base64,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" 
          alt="Limkokwing University" 
          className="sidebar-logo mb-2"
          style={{ maxWidth: '120px', height: 'auto' }}
        />
      </div>
      
      <div className="text-center mb-4">
        <div className="profile-placeholder bg-secondary rounded-circle mx-auto mb-2 d-flex align-items-center justify-content-center text-white">
          <i className="fas fa-chalkboard-teacher fa-2x"></i>
        </div>
        <h6>{user.name}</h6>
        <small>Lecturer</small>
      </div>

      <nav className="nav flex-column">
        <Link to="/lecturer/dashboard" className={`nav-link text-white ${location.pathname === '/lecturer/dashboard' ? 'active bg-primary' : ''}`}>
          <i className="fas fa-tachometer-alt me-2"></i> Dashboard
        </Link>
        <Link to="/lecturer/classes" className={`nav-link text-white ${location.pathname === '/lecturer/classes' ? 'active bg-primary' : ''}`}>
          <i className="fas fa-chalkboard me-2"></i> Classes
        </Link>
        <Link to="/lecturer/reports" className={`nav-link text-white ${location.pathname === '/lecturer/reports' ? 'active bg-primary' : ''}`}>
          <i className="fas fa-file-alt me-2"></i> Reports
        </Link>
        <Link to="/lecturer/monitoring" className={`nav-link text-white ${location.pathname === '/lecturer/monitoring' ? 'active bg-primary' : ''}`}>
          <i className="fas fa-chart-bar me-2"></i> Monitoring
        </Link>
        <Link to="/lecturer/rating" className={`nav-link text-white ${location.pathname === '/lecturer/rating' ? 'active bg-primary' : ''}`}>
          <i className="fas fa-star me-2"></i> Rating
        </Link>
        <button className="nav-link text-white text-start border-0 bg-transparent" onClick={onLogout}>
          <i className="fas fa-sign-out-alt me-2"></i> Logout
        </button>
      </nav>
    </div>
  );
};

const PrincipalLecturerSidebar = ({ user, onLogout }) => {
  const location = useLocation();
  
  return (
    <div className="col-md-2 sidebar d-none d-md-block bg-dark text-white">
      <div className="text-center mb-3 pt-3">
        <img 
          src="https://th.bing.com/th/id/OIP.FZjOcNetgkfRJhQkSR0acQHaD4?o=7rm=3&rs=1&pid=ImgDetMain&o=7&rm=3" 
          alt="Limkokwing University" 
          className="sidebar-logo mb-2"
          style={{ maxWidth: '120px', height: 'auto' }}
        />
      </div>
      
      <div className="text-center mb-4">
        <div className="profile-placeholder bg-secondary rounded-circle mx-auto mb-2 d-flex align-items-center justify-content-center text-white">
          <i className="fas fa-user-tie fa-2x"></i>
        </div>
        <h6>{user.name}</h6>
        <small>Principal Lecturer</small>
      </div>

      <nav className="nav flex-column">
        <Link to="/prl/dashboard" className={`nav-link text-white ${location.pathname === '/prl/dashboard' ? 'active bg-primary' : ''}`}>
          <i className="fas fa-tachometer-alt me-2"></i> Dashboard
        </Link>
        <Link to="/prl/courses" className={`nav-link text-white ${location.pathname === '/prl/courses' ? 'active bg-primary' : ''}`}>
          <i className="fas fa-book me-2"></i> Courses
        </Link>
        <Link to="/prl/reports" className={`nav-link text-white ${location.pathname === '/prl/reports' ? 'active bg-primary' : ''}`}>
          <i className="fas fa-file-alt me-2"></i> Reports
        </Link>
        <Link to="/prl/monitoring" className={`nav-link text-white ${location.pathname === '/prl/monitoring' ? 'active bg-primary' : ''}`}>
          <i className="fas fa-chart-bar me-2"></i> Monitoring
        </Link>
        <Link to="/prl/rating" className={`nav-link text-white ${location.pathname === '/prl/rating' ? 'active bg-primary' : ''}`}>
          <i className="fas fa-star me-2"></i> Rating
        </Link>
        <Link to="/prl/classes" className={`nav-link text-white ${location.pathname === '/prl/classes' ? 'active bg-primary' : ''}`}>
          <i className="fas fa-chalkboard me-2"></i> Classes
        </Link>
        <button className="nav-link text-white text-start border-0 bg-transparent" onClick={onLogout}>
          <i className="fas fa-sign-out-alt me-2"></i> Logout
        </button>
      </nav>
    </div>
  );
};

const ProgramLeaderSidebar = ({ user, onLogout }) => {
  const location = useLocation();
  
  return (
    <div className="col-md-2 sidebar d-none d-md-block bg-dark text-white">
      <div className="text-center mb-3 pt-3">
        <img 
          src="https://th.bing.com/th/id/OIP.FZjOcNetgkfRJhQkSR0acQHaD4?o=7rm=3&rs=1&pid=ImgDetMain&o=7&rm=3" 
          alt="Limkokwing University" 
          className="sidebar-logo mb-2"
          style={{ maxWidth: '120px', height: 'auto' }}
        />
      </div>
      
      <div className="text-center mb-4">
        <div className="profile-placeholder bg-secondary rounded-circle mx-auto mb-2 d-flex align-items-center justify-content-center text-white">
          <i className="fas fa-user-cog fa-2x"></i>
        </div>
        <h6>{user.name}</h6>
        <small>Program Leader</small>
      </div>

      <nav className="nav flex-column">
        <Link to="/pl/dashboard" className={`nav-link text-white ${location.pathname === '/pl/dashboard' ? 'active bg-primary' : ''}`}>
          <i className="fas fa-tachometer-alt me-2"></i> Dashboard
        </Link>
        <Link to="/pl/courses" className={`nav-link text-white ${location.pathname === '/pl/courses' ? 'active bg-primary' : ''}`}>
          <i className="fas fa-book me-2"></i> Courses
        </Link>
        <Link to="/pl/reports" className={`nav-link text-white ${location.pathname === '/pl/reports' ? 'active bg-primary' : ''}`}>
          <i className="fas fa-file-alt me-2"></i> Reports
        </Link>
        <Link to="/pl/monitoring" className={`nav-link text-white ${location.pathname === '/pl/monitoring' ? 'active bg-primary' : ''}`}>
          <i className="fas fa-chart-bar me-2"></i> Monitoring
        </Link>
        <Link to="/pl/classes" className={`nav-link text-white ${location.pathname === '/pl/classes' ? 'active bg-primary' : ''}`}>
          <i className="fas fa-chalkboard me-2"></i> Classes
        </Link>
        <Link to="/pl/lectures" className={`nav-link text-white ${location.pathname === '/pl/lectures' ? 'active bg-primary' : ''}`}>
          <i className="fas fa-users me-2"></i> Lectures
        </Link>
        <Link to="/pl/rating" className={`nav-link text-white ${location.pathname === '/pl/rating' ? 'active bg-primary' : ''}`}>
          <i className="fas fa-star me-2"></i> Rating
        </Link>
        <button className="nav-link text-white text-start border-0 bg-transparent" onClick={onLogout}>
          <i className="fas fa-sign-out-alt me-2"></i> Logout
        </button>
      </nav>
    </div>
  );
};

// Main App Component
function App() {
  const [user, setUser] = useState(null);
  const [lecturerReports, setLecturerReports] = useState([]);

  // Sample data
  const [studentCourses] = useState([
    { code: 'DIWA2110', name: 'Web Applications', lecturer: 'Mr Thokoana', schedule: 'Mon 10:30-12:30', venue: 'MultiMedia 5', avgRating: 4.5 },
    { code: 'DIPR2110', name: 'Object Oriented Programming', lecturer: 'Mr. Takalimane', schedule: 'Wed 14:00-16:00', venue: 'MultiMedia 4', avgRating: 4.2 },
    { code: 'DIDB2110', name: 'Human Computer Interaction', lecturer: 'Ms Sekopo', schedule: 'Fri 09:00-11:00', venue: 'MultiMedia 1', avgRating: 4.7 }
  ]);

  const [studentAttendance] = useState([
    { studentId: 'ST001', date: '2025-09-15', course: 'DIWA2110', status: 'Present', time: '10:30-12:30', remarks: 'On time' },
    { studentId: 'ST001', date: '2025-09-08', course: 'DIWA2110', status: 'Present', time: '10:30-12:30', remarks: 'On time' },
    { studentId: 'ST001', date: '2025-09-01', course: 'DIWA2110', status: 'Absent', time: '10:30-12:30', remarks: 'Sick leave' },
    { studentId: 'ST001', date: '2025-08-25', course: 'DIPR2110', status: 'Present', time: '14:00-16:00', remarks: 'On time' }
  ]);

  const handleLogin = (userData) => {
    setUser(userData);
  };

  const handleLogout = () => {
    setUser(null);
  };

  const handleRateCourse = (courseCode, rating, feedback) => {
    console.log(`Rating for ${courseCode}: ${rating} stars, Feedback: ${feedback}`);
  };

  const handleAddReport = (reportData) => {
    const newReport = {
      id: lecturerReports.length + 1,
      ...reportData,
      status: 'Submitted',
      date: new Date().toISOString().split('T')[0]
    };
    setLecturerReports([...lecturerReports, newReport]);
  };

  const renderSidebar = () => {
    switch (user.role) {
      case 'student':
        return <StudentSidebar user={user} onLogout={handleLogout} />;
      case 'lecturer':
        return <LecturerSidebar user={user} onLogout={handleLogout} />;
      case 'principal_lecturer':
        return <PrincipalLecturerSidebar user={user} onLogout={handleLogout} />;
      case 'program_leader':
        return <ProgramLeaderSidebar user={user} onLogout={handleLogout} />;
      default:
        return null;
    }
  };

  const renderRoutes = () => {
    switch (user.role) {
      case 'student':
        return (
          <Routes>
            <Route path="/student/dashboard" element={
              <StudentDashboard user={user} courses={studentCourses} attendance={studentAttendance} />
            } />
            <Route path="/student/monitoring" element={
              <StudentMonitoring user={user} attendance={studentAttendance} />
            } />
            <Route path="/student/rating" element={
              <StudentRating user={user} courses={studentCourses} onRateCourse={handleRateCourse} />
            } />
            <Route path="/" element={<Navigate to="/student/dashboard" replace />} />
          </Routes>
        );
      case 'lecturer':
        return (
          <Routes>
            <Route path="/lecturer/dashboard" element={<LecturerDashboard user={user} />} />
            <Route path="/lecturer/classes" element={<LecturerClasses user={user} />} />
            <Route path="/lecturer/reports" element={<LecturerReports user={user} onAddReport={handleAddReport} />} />
            <Route path="/lecturer/monitoring" element={<LecturerMonitoring user={user} />} />
            <Route path="/lecturer/rating" element={<LecturerRating user={user} />} />
            <Route path="/" element={<Navigate to="/lecturer/dashboard" replace />} />
          </Routes>
        );
      case 'principal_lecturer':
        return (
          <Routes>
            <Route path="/prl/dashboard" element={<PrincipalLecturerDashboard user={user} />} />
            <Route path="/prl/courses" element={<PRLCourses user={user} />} />
            <Route path="/prl/reports" element={<PRLReports user={user} />} />
            <Route path="/prl/monitoring" element={<LecturerMonitoring user={user} />} />
            <Route path="/prl/rating" element={<LecturerRating user={user} />} />
            <Route path="/prl/classes" element={<LecturerClasses user={user} />} />
            <Route path="/" element={<Navigate to="/prl/dashboard" replace />} />
          </Routes>
        );
      case 'program_leader':
        return (
          <Routes>
            <Route path="/pl/dashboard" element={<ProgramLeaderDashboard user={user} />} />
            <Route path="/pl/courses" element={<PLCourses user={user} />} />
            <Route path="/pl/reports" element={<PLReports user={user} />} />
            <Route path="/pl/monitoring" element={<LecturerMonitoring user={user} />} />
            <Route path="/pl/classes" element={<LecturerClasses user={user} />} />
            <Route path="/pl/lectures" element={<PRLCourses user={user} />} />
            <Route path="/pl/rating" element={<LecturerRating user={user} />} />
            <Route path="/" element={<Navigate to="/pl/dashboard" replace />} />
          </Routes>
        );
      default:
        return null;
    }
  };

  const getPortalName = () => {
    switch (user?.role) {
      case 'student': return 'Student Portal';
      case 'lecturer': return 'Lecturer Portal';
      case 'principal_lecturer': return 'Principal Lecturer Portal';
      case 'program_leader': return 'Program Leader Portal';
      default: return 'LUCT Portal';
    }
  };

  return (
    <Router>
      <div>
        {!user ? (
          <Auth onLogin={handleLogin} />
        ) : (
          <>
            <nav className="navbar navbar-expand-lg navbar-light bg-white shadow-sm">
              <div className="container-fluid">
                <Link className="navbar-brand" to="/">
                  <img 
                    src="data:image/webp;base64,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" 
                    alt="Limkokwing University" 
                    className="navbar-logo me-2"
                    style={{ height: '40px' }}
                  />
                  LUCT {getPortalName()}
                </Link>
                <div className="navbar-nav ms-auto">
                  <span className="navbar-text me-3">
                    Welcome, {user.name} ({user.role.replace('_', ' ')})
                  </span>
                  <button className="btn btn-outline-secondary btn-sm" onClick={handleLogout}>
                    <i className="fas fa-sign-out-alt me-1"></i> Logout
                  </button>
                </div>
              </div>
            </nav>

            <div className="container-fluid">
              <div className="row">
                {renderSidebar()}
                
                <div className={`main-content p-4 ${user.role === 'student' ? 'col-md-10' : 'col-md-10'}`}>
                  {renderRoutes()}
                </div>
              </div>
            </div>

            <footer className="bg-primary text-white text-center py-3 mt-4">
              <p className="mb-0">© 2025 Limkokwing University of Creative Technology - Faculty Reporting System</p>
              <small>For support, contact: support@luct.ac.ls</small>
            </footer>
          </>
        )}
      </div>
    </Router>
  );
}

export default App;